import React, { useState } from "react";
import "./MapSwitcher.css";
import * as helpers from "../../helpers/helpers";
import MapConfig from "./MapSwitcherConfig.json";
import TileLayer from "ol/layer/Tile";
import WMTS, { optionsFromCapabilities } from "ol/source/WMTS";
import WMTSCapabilities from "ol/format/WMTSCapabilities";
import WebGLTileLayer from "ol/layer/WebGLTile";
import GoogleMaps from "ol/source/Google.js";
import SpatialLayerList from "./SpatialLayerList";
import LayerLegend from "./LayerLegend.jsx";
import { BACKEND } from "../../constants/api";

export default function MapSwitcher() {
	const [imagerySliderValue, setImagerySliderValue] = useState(null);
	const [imageryLayers, setImageryLayers] = useState([]);
	const [imageryPanelOpen, setImageryPanelOpen] = useState(false);
	const [streetsLayer, setStreetsLayer] = useState(null);
	const [streetsCheckbox, setStreetsCheckbox] = useState(true);
	const [containerCollapsed, setContainerCollapsed] = useState(false);
	const [topoPanelOpen, setTopoPanelOpen] = useState(false);
	const [topoLayers, setTopoLayers] = useState([]);
	const [topoActiveIndex, setTopoActiveIndex] = useState(0);
	const [activeButton, setActiveButton] = useState("imagery");
	const [allLayers, setAllLayers] = useState([]);
	const [forceUpdate, setForceUpdate] = useState([]);

	//todo: if environment is production, have it default to google maps and fall back to these free layers if it hits an error (e.g. 429: Too many requests)
	const mapLayers = [
		{
			name: "Satellite",
			isTileLayer: true,
			isBaseLayer: true,
			defaultVisible: true,
			url: "https://server.arcgisonline.com/arcgis/rest/services/World_Imagery/MapServer",
			capabilitiesUrl:
				"https://server.arcgisonline.com/arcgis/rest/services/World_Imagery/MapServer/WMTS/1.0.0/WMTSCapabilities.xml",
			layer: "World_Imagery",
			style: "default",
			matrixSet: "default028mm",
		},
		{
			name: "Roads",
			isTileLayer: true,
			isBaseLayer: false,
			defaultVisible: true,
			url: "https://server.arcgisonline.com/arcgis/rest/services/Reference/World_Transportation/MapServer/WMTS/",
			capabilitiesUrl:
				"https://server.arcgisonline.com/arcgis/rest/services/Reference/World_Transportation/MapServer/WMTS/1.0.0/WMTSCapabilities.xml",
			layer: "Reference_World_Transportation",
			style: "default",
			matrixSet: "default028mm",
			format: "image/png",
		},
		{
			name: "Cities & Counties",
			isTileLayer: true,
			isBaseLayer: false,
			defaultVisible: true,
			url: "https://server.arcgisonline.com/arcgis/rest/services/Reference/World_Boundaries_and_Places/MapServer/WMTS",
			capabilitiesUrl:
				"https://server.arcgisonline.com/arcgis/rest/services/Reference/World_Boundaries_and_Places/MapServer/WMTS/1.0.0/WMTSCapabilities.xml",
			layer: "Reference_World_Boundaries_and_Places",
			style: "default",
			matrixSet: "default028mm",
			format: "image/png",
		},
	];

	// LISTEN FOR MAP TO MOUNT
	window.emitter.addListener("mapLoaded", () => onMapLoad());

	const addLayersToState = (keys) => {
		let newList = [];

		if (keys)
			if (keys.length > 0) {
				keys.forEach((key) => {
					if (!allLayers.includes(key)) {
						newList.push(key);
					}
				});
			}

		var allLayersPlusNew = allLayers.concat(newList);

		setAllLayers(allLayersPlusNew);
	};

	const onMapLoad = async () => {
		let newLayers = [];

		for (var i = 0; i < mapLayers.length; i++) {
			const layerConfig = mapLayers[i];

			if (layerConfig.isTileLayer && layerConfig.name == "ssurgoSoils") {
				/* window.map.addLayer(new TileLayer({
                    title: "Soils Imagery",
                    source: new XYZ(
                        {
                            tileUrlFunction: getSsurgoTileImage
                        }),
                    visible: false
                }));
                
                newLayers.push("Soils Imagery")*/
			} else if (layerConfig.isTileLayer && layerConfig.capabilitiesUrl) {
				try {
					// Fetch WMTS capabilities
					const response = await fetch(layerConfig.capabilitiesUrl);
					if (!response.ok) {
						console.error(`Failed to fetch WMTS capabilities for ${layerConfig.name}:`, response.statusText);
						continue;
					}

					const capabilitiesText = await response.text();
					const parser = new WMTSCapabilities();
					const capabilities = parser.read(capabilitiesText);

					// Use optionsFromCapabilities to get WMTS options
					const options = optionsFromCapabilities(capabilities, {
						layer: layerConfig.layer,
						matrixSet: layerConfig.matrixSet,
						style: layerConfig.style,
						format: layerConfig.format,
					});

					if (!options) {
						console.error(`Could not create WMTS options for ${layerConfig.name}`);
						continue;
					}

					// Create WMTS source with options from capabilities
					const wmtsSource = new WMTS(options);

					// Create and add tile layer
					const tileLayer = new TileLayer({
						visible: layerConfig.defaultVisible,
						title: layerConfig.name,
						source: wmtsSource,
					});

					window.map.addLayer(tileLayer);

					// Add to newLayers array for SpatialLayerList
					newLayers.push(layerConfig.name);
				} catch (error) {
					console.error(`Error processing WMTS layer ${layerConfig.name}:`, error);

					// Fallback to original implementation if capabilities fetch fails
					// window.map.addLayer(
					// 	new TileLayer({
					// 		visible: true,
					// 		title: layerConfig.name,
					// 		source: new WMTS({
					// 			url: layerConfig.url,
					// 			layer: layerConfig.layer,
					// 			style: layerConfig.style,
					// 			matrixSet: layerConfig.matrixSet,
					// 			format: layerConfig.format,
					// 		}),
					// 	})
					// );
				}
			}
		}

		if (MapConfig) {
			if (MapConfig.cdlServices)
				MapConfig.cdlServices.map((service) => {
					let layer = helpers.getImageWMSLayerCDL(service.url, service.layers, service.name, service.version);
					layer.setOpacity(0.6);
					layer.setVisible(false);
					window.map.addLayer(layer);

					newLayers.push(service.name);
				});

			if (MapConfig.geoserverServices)
				MapConfig.geoserverServices.map((service) => {
					let layer = helpers.getImageWMSLayer(service.url, service.layers, service.name, service.version);
					layer.setOpacity(0.6);
					layer.setVisible(false);
					window.map.addLayer(layer);

					newLayers.push(service.name);
				});

			/*Turning off for now */
			if (!MapConfig.mapServerServices)
				MapConfig.mapServerServices.map((service) => {
					let layer = helpers.getMapServerVectorSource(service.url, service.layers, service.name, service.version);
					layer.setOpacity(0.9);
					layer.setVisible(false);
					window.map.addLayer(layer);

					newLayers.push(service.name);
				});

			/*locally hosted postgis layers.  Fetch them first. */
			if (MapConfig.postgresServerServices)
				MapConfig.postgresServerServices.map((service) => {
					if (service.url) {
						getPostgresLayer(service);
					}

					if (service.name != "States") {
						newLayers.push(service.name);
					}
				});
		}
		addLayersToState(newLayers);
	};

	const getPostgresLayer = (service) => {
		var data = { Fips: [] };

		fetch(`${BACKEND}${service.url}`, {
			method: "POST",
			body: JSON.stringify(data),
			headers: { "Content-Type": "application/json" },
		})
			.then((response) => {
				if (response.status == 200) {
					response
						.json()
						.then((geoJson) => {
							let layer = helpers.getPostgresServerVectorSource(
								service.url,
								service.layers,
								service.name,
								service.version,
								"",
								geoJson
							);
							if (layer) {
								if (service.name == "States") setStateLayerOnMap(layer);
								else if (service.name == "Counties") setCountyLayerOnMap(layer);
								else {
									layer.setOpacity(0.9);
									layer.setVisible(false);
									window.map.addLayer(layer);
								}

								//addLayersToState([service.name]);
							}
							//addLayersToState([layer] );
						})
						.catch((e) => {
							console.log("Layer Not Found", e);
						});
				}
			})
			.catch((e) => {
				console.log("Layer Not Found", e);
			});
	};

	/*
      Remove any existing state layers besides new layer
      TODO: Just hide layers so we don't need to fetch them again.
    */
	const setStateLayerOnMap = (newLayer) => {
		var layersToRemove = [];
		var featureLayers = [];

		window.map.getLayers().forEach((layer) => {
			let title = layer.get("title");
			let name = layer.get("name"); //Yes, we have some layers that use "title" and some that use "name". I'll fix this if I have a chance. Kids, don't be like Sweep when you grow up.

			if (title !== null)
				if (title === "States") {
					layersToRemove.push(layer);
				}

			if (name && (name === "fieldSidebar" || name === "prepumpslayer")) {
				featureLayers.push(layer);
			}
		});

		layersToRemove.forEach((layerToRemove) => {
			window.map.removeLayer(layerToRemove);
		});

		var firstFeature = newLayer.getSource().getFeatures()[0];

		if (!featureLayers.some((layer) => layer.getSource().getFeatures().length > 0)) {
			helpers.zoomToFeature(firstFeature);
		}

		newLayer.setOpacity(0.9);
		newLayer.setVisible(true);
		window.map.addLayer(newLayer);
	};

	/*
      Remove any existing county layers besides new layers
      TODO: Just hide layers so we don't need to fetch them again.
    */
	const setCountyLayerOnMap = (newLayer) => {
		var layersToRemove = [];

		window.map.getLayers().forEach((layer) => {
			let title = layer.get("title");

			if (title !== null)
				if (title === "Counties") {
					layersToRemove.push(layer);
				}
		});

		layersToRemove.forEach((layerToRemove) => {
			window.map.removeLayer(layerToRemove);
		});

		newLayer.setOpacity(0.9);
		newLayer.setVisible(true);
		window.map.addLayer(newLayer);
	};

	const getSsurgoTileImage = (bounds) => {
		var tileObj = {
			z: bounds[0],
			x: bounds[1],
			y: bounds[2],
		};

		var tileBounds = helpers.getTileBounds(tileObj);
		var tileExtent = [tileBounds.sw.lng, -1 * tileBounds.ne.lat, tileBounds.ne.lng, -1 * tileBounds.sw.lat];

		var url = "https://SDMDataAccess.sc.egov.usda.gov/Spatial/SDM.wms";
		url += "?REQUEST=GetMap";
		url += "&SERVICE=WMS";
		url += "&VERSION=1.1.1";
		url += "&LAYERS=" + "MapunitPoly";
		url += "&FORMAT=" + "image/png";
		url += "&TRANSPARENT=" + "true";
		url += "&SRS=" + "EPSG:4326";
		url += "&BBOX=" + tileExtent;
		url += "&WIDTH=" + "256";
		url += "&HEIGHT=" + "256";
		return url;
	};

	// CALLED WHEN SLIDING OR TO RESET
	const updateImageryLayers = (value) => {
		for (let index = 0; index < imageryLayers.length; index++) {
			let layer = imageryLayers[index];
			if (value === -1) layer.setVisible(false);
			else {
				const layerIndex = layer.getProperties().index;
				const indexRatio = 1 - Math.abs(layerIndex - value);
				if (layerIndex === value) {
					layer.setOpacity(1);
					layer.setVisible(true);
				} else if (indexRatio < 0) {
					layer.setOpacity(0);
					layer.setVisible(false);
				} else {
					layer.setOpacity(indexRatio);
					layer.setVisible(true);
				}
			}
		}
	};

	const onCollapsedClick = (evt) => {
		// HIDE OPEN PANELS
		if (containerCollapsed === false) {
			setImageryPanelOpen(false);
			setTopoPanelOpen(false);
		}

		setContainerCollapsed(!containerCollapsed);
	};

	const showLegendClicked = () => {
		if (activeButton === "legend") setActiveButton("imagery");
		else setActiveButton("legend");
	};

	const setLayerVisibilty = (value, key) => {
		window.map.getLayers().forEach((layer) => {
			let title = layer.get("title");

			if (title !== null)
				if (title === key) {
					layer.setVisible(value);
					setForceUpdate(value);
				}
		});
	};

	const onSpatialLayerClicked = (e, key) => {
		if (e)
			if (e.target) {
				setLayerVisibilty(e.target.checked, key);
			}
	};

	let visibleLayers = helpers.getVisibleLayers();

	return (
		<div>
			<div id="sc-map-main-container">
				<div
					id="sc-map-collapse-button"
					className={containerCollapsed ? "sc-map-collapse-button closed" : "sc-map-collapse-button"}
					onClick={onCollapsedClick}
				/>
				<div className={containerCollapsed ? "sc-hidden" : "sc-map-topo"}>
					<SpatialLayerList visibleLayers={visibleLayers} allLayers={allLayers} onChange={onSpatialLayerClicked} />
					{/*<button*/}
					{/*  className={*/}
					{/*    legend === "legend"*/}
					{/*      ? "sc-button sc-map-topo-button active"*/}
					{/*      : "sc-button sc-map-topo-button"*/}
					{/*  }*/}
					{/*  onClick={showLegendClicked}*/}
					{/*>*/}
					{/*  {activeButton === "legend" ? "Hide Legend" : "Show Legend"}*/}
					{/*</button>*/}
				</div>

				{activeButton === "legend" && (
					<div className={"sc-legend"}>
						{visibleLayers &&
							visibleLayers.length > 0 &&
							visibleLayers.map((layer, index) => (
								<div className={"sc-legend-item"} key={layer + index.toString()}>
									<LayerLegend layer={layer} />
								</div>
							))}
					</div>
				)}
			</div>
		</div>
	);
}

// IMPORT ALL IMAGES
const images = importAllImages(require.context("./images", false, /\.(png|jpe?g|svg|gif)$/));
function importAllImages(r) {
	let images = {};
	r.keys().map((item, index) => (images[item.replace("./", "")] = r(item)));
	return images;
}
